import { FC } from 'react';
import { Check } from 'lucide-react';

import type { CourseOption } from '@teachur/http-client';
import { cn } from '@new/lib';

interface CourseBuilderItemProps {
  course: CourseOption;
  isSelected: boolean;
  disabled?: boolean;
  disabledReason?: string | null;
  selectable?: boolean;
  countsTowardLabel: string;
  onToggle: (courseId: number) => void;
}

export const CourseBuilderItem: FC<CourseBuilderItemProps> = ({
  course,
  isSelected,
  disabled = false,
  disabledReason = null,
  selectable = true,
  countsTowardLabel,
  onToggle,
}) => {
  const onClick = () => {
    if (!disabled) {
      onToggle(course.courseId);
    }
  };

  const showAsSelected = selectable && isSelected;

  return (
    <div
      onClick={onClick}
      role={selectable ? 'checkbox' : 'button'}
      aria-checked={selectable ? isSelected : undefined}
      aria-disabled={disabled}
      className={cn(
        'flex items-start justify-between gap-3 rounded-xl border p-4 transition-colors',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        showAsSelected
          ? 'border-(--primary-O900) bg-(--primary-O200)'
          : 'border-(--neutral-black-NB200) hover:border-(--primary-O400)'
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <h4 className="m-0! [font-size:var(--fs-b4)] font-semibold! text-(--neutral-black-NB1000)!">
            {course.name}
          </h4>
          <span className="[font-size:var(--fs-l5)] text-(--neutral-black-NB600)!">
            {course.credits} {course.credits === 1 ? 'credit' : 'credits'}
          </span>
        </div>
        <p className="m-0! [font-size:var(--fs-l5)] text-(--neutral-black-NB600)!">
          {disabled && disabledReason ? disabledReason : 'Elective credit toward your plan.'}
        </p>
        {showAsSelected ? (
          <p className="m-0! [font-size:var(--fs-l5)] font-semibold! text-(--primary-O900)!">
            Count towards {countsTowardLabel}
          </p>
        ) : null}
      </div>

      {selectable ? (
        <span
          className={cn(
            'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border',
            isSelected
              ? 'border-(--primary-O900) bg-(--primary-O900)'
              : 'border-(--neutral-black-NB300)'
          )}
        >
          {isSelected ? (
            <Check size={12} strokeWidth={3} className="text-(--neutral-white-NW1000)" />
          ) : null}
        </span>
      ) : null}
    </div>
  );
};
